import L from 'leaflet';
import { sizeCanvas } from './canvasSize.js';

// Faster versions of the Leaflet steps the map spends its time in, each giving exactly what Leaflet itself
// gives (the same points, bounds, parts and draw order), so nothing on the map looks or behaves differently.
// Imported once by both views. Measured with Stockholms län picked (3,363 areas, ~11,000 homes).
//
// 1. Projection. On every zoom Leaflet projects every point of every area again: per point a LatLng check,
//    a sine and a logarithm (Web Mercator), the CRS transformation and four throwaway Points. The part that
//    doesn't depend on the zoom — the point's place in the unit square, Leaflet's own `a * x + b` — is
//    computed once per outline ring and kept (a Float64Array per ring, in a WeakMap, so it goes with the
//    ring). A zoom then only scales, rounds and shifts it, with the same arithmetic in the same order, so
//    every projected point is bit for bit Leaflet's.
// 2. Clipping and simplifying. A polygon is clipped to the renderer's bounds on every pan and zoom (four passes
//    over every point, a new array per pass) and then simplified (Douglas-Peucker). One lying wholly inside the
//    bounds comes out of clipping unchanged, so it is taken as is, and its simplified rings are kept until it's
//    projected again (the next zoom): a pan at the same zoom gets the very same arrays back.
// 3. Drawing. With those arrays unchanged, each area's canvas path is kept as a Path2D, so a redraw (every pan,
//    and every frame of a hover fading in) fills and strokes it in one call instead of a lineTo per point. The
//    path is the same commands, so the same pixels.
// 4. Partial redraws and hit tests. A canvas renderer redraws a dirty rectangle (a hover fading in, an area
//    toggled) by walking all of its layers in draw order to find the ones touching it, and finds the layer
//    under the pointer the same way on every mouse move and click: 3,363 areas each time. Each canvas keeps a
//    grid of its layers' pixel bounds instead, rebuilt whenever a layer's bounds or the draw order change, and
//    walks only the candidates, still in draw order.
// 5. Canvas size. Leaflet sizes each canvas renderer's bitmap again on every moveend (Canvas._update), which
//    reallocates it even at the same size: the area and dot canvases are ~4320×2700 and the selection overlay's
//    5760×3600 on a retina screen. At the same size it is reset instead (ctx.reset(): cleared and its state back
//    to the defaults, which is all a resize does to what's drawn), so the memory isn't thrown away and taken
//    again on every pan.

const SPHERICAL_MERCATOR = L.Projection.SphericalMercator;
const EPSG3857 = L.CRS.EPSG3857;

// --- 1. Projection ---------------------------------------------------------------------------------------

// ring (an array of LatLng) -> { uv, length, first, last }; uv holds Leaflet's `a * x + b` / `c * y + d`.
const unitCache = new WeakMap();

const unitCoordinates = (ring) => {
    const length = ring.length;
    const cached = unitCache.get(ring);
    // A ring changed in place (a point added, or the ends swapped) is computed again.
    if (cached && cached.length === length && cached.first === ring[0] && cached.last === ring[length - 1]) {
        return cached.uv;
    }
    const { R, MAX_LATITUDE } = SPHERICAL_MERCATOR;
    const t = EPSG3857.transformation;
    const uv = new Float64Array(length * 2);
    for (let i = 0; i < length; i += 1) {
        const latlng = ring[i];
        // Leaflet's SphericalMercator.project and Transformation._transform, step for step.
        const d = Math.PI / 180;
        const lat = Math.max(Math.min(MAX_LATITUDE, latlng.lat), -MAX_LATITUDE);
        const sin = Math.sin(lat * d);
        const x = R * latlng.lng * d;
        const y = R * Math.log((1 + sin) / (1 - sin)) / 2;
        uv[i * 2] = t._a * x + t._b;
        uv[(i * 2) + 1] = t._c * y + t._d;
    }
    unitCache.set(ring, { uv, length, first: ring[0], last: ring[length - 1] });
    return uv;
};

const polylineProto = L.Polyline.prototype;
if (!polylineProto.__fastProjection) {
    polylineProto.__fastProjection = true;
    const projectLatlngs = polylineProto._projectLatlngs;

    polylineProto._projectLatlngs = function fastProjectLatlngs(latlngs, result, projectedBounds) {
        // Nested rings (Leaflet's non-flat branch): each one by itself, through this method again.
        if (!(latlngs[0] instanceof L.LatLng)) {
            for (let i = 0; i < latlngs.length; i += 1) this._projectLatlngs(latlngs[i], result, projectedBounds);
            return;
        }
        const map = this._map;
        if (!map || map.options.crs !== EPSG3857) {
            projectLatlngs.call(this, latlngs, result, projectedBounds);
            return;
        }
        // map.latLngToLayerPoint: project at the map's zoom, round, subtract the pixel origin.
        const scale = map.options.crs.scale(map._zoom);
        const origin = map.getPixelOrigin();
        const ox = origin.x;
        const oy = origin.y;
        const uv = unitCoordinates(latlngs);
        const length = latlngs.length;
        const ring = new Array(length);
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (let i = 0; i < length; i += 1) {
            const x = Math.round(scale * uv[i * 2]) - ox;
            const y = Math.round(scale * uv[(i * 2) + 1]) - oy;
            ring[i] = new L.Point(x, y);
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
        // Extending by the corners gives the same bounds as extending by every point.
        if (length) {
            projectedBounds.extend(new L.Point(minX, minY));
            projectedBounds.extend(new L.Point(maxX, maxY));
        }
        result.push(ring);
    };
}

// --- 2. Clipping and simplifying ---------------------------------------------------------------------------------------------

const polygonProto = L.Polygon.prototype;
if (!polygonProto.__fastClip) {
    polygonProto.__fastClip = true;
    const clipPoints = polygonProto._clipPoints;

    polygonProto._clipPoints = function fastClipPoints() {
        this.__partsSimplified = false;
        const renderer = this._renderer;
        const raw = this._rawPxBounds;
        const view = renderer && renderer._bounds;
        if (raw && view && this._pxBounds && !this.options.noClip) {
            // Leaflet clips against the renderer's bounds grown by the stroke width (Polygon._clipPoints).
            const w = this.options.weight;
            if (raw.min.x >= view.min.x - w && raw.max.x <= view.max.x + w
                && raw.min.y >= view.min.y - w && raw.max.y <= view.max.y + w) {
                // Every point inside (bit code 0): clipPolygon would hand back each ring as it is, and
                // _simplifyPoints would then simplify each (simplify doesn't change its input, so the rings
                // themselves give the same result). Kept per projection: a pan at this zoom reuses them.
                const tolerance = this.options.smoothFactor;
                let cache = this.__simplified;
                if (!cache || cache.rings !== this._rings || cache.tolerance !== tolerance) {
                    const parts = [];
                    const rings = this._rings;
                    for (let i = 0; i < rings.length; i += 1) {
                        if (rings[i].length) parts.push(L.LineUtil.simplify(rings[i], tolerance));
                    }
                    cache = { rings, tolerance, parts };
                    this.__simplified = cache;
                }
                this._parts = cache.parts.slice();
                this.__partsSimplified = true;
                return;
            }
        }
        clipPoints.call(this);
    };
}

// _update runs _clipPoints, _simplifyPoints and _updatePath in that order: parts the fast clip already
// simplified are left alone (simplifying twice would not give the same points).
if (!polylineProto.__fastSimplify) {
    polylineProto.__fastSimplify = true;
    const simplifyPoints = polylineProto._simplifyPoints;
    polylineProto._simplifyPoints = function fastSimplifyPoints() {
        if (this.__partsSimplified) {
            this.__partsSimplified = false;
            return;
        }
        simplifyPoints.call(this);
    };
}

// --- 3. Drawing: a kept Path2D per layer -------------------------------------------------------------------

// Canvas._fillStroke, with the path passed in.
const fillStrokePath = (ctx, layer, path) => {
    const { options } = layer;
    if (options.fill) {
        ctx.globalAlpha = options.fillOpacity;
        ctx.fillStyle = options.fillColor || options.color;
        ctx.fill(path, options.fillRule || 'evenodd');
    }
    if (options.stroke && options.weight !== 0) {
        if (ctx.setLineDash) ctx.setLineDash((layer.options && layer.options._dashArray) || []);
        ctx.globalAlpha = options.opacity;
        ctx.lineWidth = options.weight;
        ctx.strokeStyle = options.color;
        ctx.lineCap = options.lineCap;
        ctx.lineJoin = options.lineJoin;
        ctx.stroke(path);
    }
};

if (!L.Canvas.prototype.__pathCache && typeof window !== 'undefined' && typeof window.Path2D === 'function') {
    L.Canvas.prototype.__pathCache = true;
    L.Canvas.prototype._updatePoly = function cachedUpdatePoly(layer, closed) {
        if (!this._drawing) return;
        const parts = layer._parts;
        const length = parts.length;
        if (!length) return;
        let cached = layer.__path2d;
        let same = Boolean(cached) && cached.closed === closed && cached.parts.length === length;
        for (let i = 0; same && i < length; i += 1) same = cached.parts[i] === parts[i];
        if (!same) {
            // Canvas._updatePoly's path, command for command.
            const path = new window.Path2D();
            for (let i = 0; i < length; i += 1) {
                const part = parts[i];
                for (let j = 0; j < part.length; j += 1) {
                    const p = part[j];
                    if (j) path.lineTo(p.x, p.y); else path.moveTo(p.x, p.y);
                }
                if (closed) path.closePath();
            }
            cached = { path, parts: parts.slice(), closed };
            layer.__path2d = cached;
        }
        fillStrokePath(this._ctx, layer, cached.path);
    };
}

// --- 4. Partial redraws and hit tests: a grid of each canvas's layers ------------------------------------

const CELL = 256; // px
// A layer covering more cells than this (a huge area close in) is kept in a list checked every time instead.
const MAX_CELLS = 64;
const cellKey = (cx, cy) => ((cx + 2097152) * 4194304) + (cy + 2097152);

const markDirty = (layer) => {
    const renderer = layer && layer._renderer;
    if (renderer) renderer.__gridDirty = true;
};

// Every way a layer's pixel bounds change (Polyline / CircleMarker _updateBounds, the only writers of
// _pxBounds) and every change to a canvas's layers or their order makes its grid stale.
[L.Polyline.prototype, L.CircleMarker.prototype].forEach((proto) => {
    if (proto.__gridBounds) return;
    proto.__gridBounds = true;
    const updateBounds = proto._updateBounds;
    proto._updateBounds = function gridUpdateBounds(...args) {
        const result = updateBounds.apply(this, args);
        markDirty(this);
        return result;
    };
});

const canvasProto = L.Canvas.prototype;
if (!canvasProto.__layerGrid) {
    canvasProto.__layerGrid = true;
    // Canvas's own methods only: wrapping one it inherits from L.Renderer (_onZoomEnd, _reset) here would
    // shadow leafletRendererGuard.js's patch of it. Those re-project every layer, which marks the grid stale
    // through _updateBounds anyway.
    ['_bringToFront', '_bringToBack', '_destroyContainer'].forEach((name) => {
        const method = canvasProto[name];
        if (typeof method !== 'function') return;
        canvasProto[name] = function gridDirty(...args) {
            this.__gridDirty = true;
            return method.apply(this, args);
        };
    });
    // The layer count decides whether the grid is worth it (kept here rather than counted on every draw).
    const initPath = canvasProto._initPath;
    canvasProto._initPath = function gridInitPath(layer) {
        this.__gridDirty = true;
        if (!this._layers[L.Util.stamp(layer)]) this.__layerCount = (this.__layerCount || 0) + 1;
        return initPath.call(this, layer);
    };
    const removePath = canvasProto._removePath;
    canvasProto._removePath = function gridRemovePath(layer) {
        this.__gridDirty = true;
        if (this._layers[L.Util.stamp(layer)]) this.__layerCount = Math.max(0, (this.__layerCount || 0) - 1);
        return removePath.call(this, layer);
    };

    // { cells: Map(key -> [layer]), big: [layer] }, layers numbered in draw order (__gridOrder).
    const buildGrid = (renderer) => {
        const cells = new Map();
        const big = [];
        let index = 0;
        for (let order = renderer._drawFirst; order; order = order.next) {
            const layer = order.layer;
            layer.__gridOrder = index;
            index += 1;
            const b = layer._pxBounds;
            if (!b) continue;
            const x0 = Math.floor(b.min.x / CELL);
            const x1 = Math.floor(b.max.x / CELL);
            const y0 = Math.floor(b.min.y / CELL);
            const y1 = Math.floor(b.max.y / CELL);
            if ((x1 - x0 + 1) * (y1 - y0 + 1) > MAX_CELLS) {
                big.push(layer);
                continue;
            }
            for (let cx = x0; cx <= x1; cx += 1) {
                for (let cy = y0; cy <= y1; cy += 1) {
                    const key = cellKey(cx, cy);
                    const list = cells.get(key);
                    if (list) list.push(layer); else cells.set(key, [layer]);
                }
            }
        }
        renderer.__grid = { cells, big, count: index };
        renderer.__gridDirty = false;
    };

    let queryStamp = 0;
    // The layers whose grid cells overlap [x0, x1] × [y0, y1], in draw order.
    const candidates = (renderer, x0, y0, x1, y1) => {
        if (renderer.__gridDirty || !renderer.__grid) buildGrid(renderer);
        const { cells, big } = renderer.__grid;
        queryStamp += 1;
        const found = [];
        const take = (layer) => {
            if (layer.__gridQuery === queryStamp) return;
            layer.__gridQuery = queryStamp;
            found.push(layer);
        };
        const cx0 = Math.floor(x0 / CELL);
        const cx1 = Math.floor(x1 / CELL);
        const cy0 = Math.floor(y0 / CELL);
        const cy1 = Math.floor(y1 / CELL);
        if ((cx1 - cx0 + 1) * (cy1 - cy0 + 1) > cells.size) {
            cells.forEach((list) => list.forEach(take));
        } else {
            for (let cx = cx0; cx <= cx1; cx += 1) {
                for (let cy = cy0; cy <= cy1; cy += 1) {
                    const list = cells.get(cellKey(cx, cy));
                    if (list) list.forEach(take);
                }
            }
        }
        big.forEach(take);
        found.sort((a, b) => a.__gridOrder - b.__gridOrder);
        return found;
    };
    // Only worth it with many layers; the few-layer canvases keep Leaflet's own walk.
    const gridPaysOff = (renderer) => Boolean(renderer._drawFirst) && renderer.__layerCount > 200;

    const draw = canvasProto._draw;
    canvasProto._draw = function gridDraw() {
        const bounds = this._redrawBounds;
        if (!bounds || !gridPaysOff(this)) {
            draw.call(this);
            return;
        }
        // Leaflet's Canvas._draw, walking only the candidates (still in draw order).
        this._ctx.save();
        const size = bounds.getSize();
        this._ctx.beginPath();
        this._ctx.rect(bounds.min.x, bounds.min.y, size.x, size.y);
        this._ctx.clip();
        this._drawing = true;
        const list = candidates(this, bounds.min.x, bounds.min.y, bounds.max.x, bounds.max.y);
        for (let i = 0; i < list.length; i += 1) {
            const layer = list[i];
            if (layer._pxBounds && layer._pxBounds.intersects(bounds)) layer._updatePath();
        }
        this._drawing = false;
        this._ctx.restore();
    };

    // Canvas._onClick and Canvas._handleMouseHover: the last interactive layer in draw order containing the
    // point wins; only the layers in the point's cell can contain it.
    const atPoint = (renderer, point) => candidates(renderer, point.x, point.y, point.x, point.y);

    const onClick = canvasProto._onClick;
    canvasProto._onClick = function gridOnClick(e) {
        if (!gridPaysOff(this)) {
            onClick.call(this, e);
            return;
        }
        const point = this._map.mouseEventToLayerPoint(e);
        let clickedLayer;
        const list = atPoint(this, point);
        for (let i = 0; i < list.length; i += 1) {
            const layer = list[i];
            if (layer.options.interactive && layer._containsPoint(point)) {
                if (!(e.type === 'click' || e.type === 'preclick') || !this._map._draggableMoved(layer)) {
                    clickedLayer = layer;
                }
            }
        }
        this._fireEvent(clickedLayer ? [clickedLayer] : false, e);
    };

    const handleMouseHover = canvasProto._handleMouseHover;
    canvasProto._handleMouseHover = function gridHandleMouseHover(e, point) {
        if (!gridPaysOff(this)) {
            handleMouseHover.call(this, e, point);
            return;
        }
        if (this._mouseHoverThrottled) return;
        let candidateHoveredLayer;
        const list = atPoint(this, point);
        for (let i = 0; i < list.length; i += 1) {
            const layer = list[i];
            if (layer.options.interactive && layer._containsPoint(point)) candidateHoveredLayer = layer;
        }
        if (candidateHoveredLayer !== this._hoveredLayer) {
            this._handleMouseOut(e);
            if (candidateHoveredLayer) {
                L.DomUtil.addClass(this._container, 'leaflet-interactive'); // change cursor
                this._fireEvent([candidateHoveredLayer], e, 'mouseover');
                this._hoveredLayer = candidateHoveredLayer;
            }
        }
        this._fireEvent(this._hoveredLayer ? [this._hoveredLayer] : false, e);
        this._mouseHoverThrottled = true;
        setTimeout(L.Util.bind(function unthrottle() {
            this._mouseHoverThrottled = false;
        }, this), 32);
    };
}

// --- 5. Canvas size -------------------------------------------------------------------------------------------

if (!L.Canvas.prototype.__sizeInPlace) {
    L.Canvas.prototype.__sizeInPlace = true;
    // Leaflet's Canvas._update (1.9.4) with sizeCanvas for the two width/height assignments.
    L.Canvas.prototype._update = function sizedUpdate() {
        if (this._map._animatingZoom && this._bounds) return;
        L.Renderer.prototype._update.call(this);
        const b = this._bounds;
        const container = this._container;
        const size = b.getSize();
        const m = L.Browser.retina ? 2 : 1;
        L.DomUtil.setPosition(container, b.min);
        sizeCanvas(container, this._ctx, m * size.x, m * size.y);
        container.style.width = `${size.x}px`;
        container.style.height = `${size.y}px`;
        if (L.Browser.retina) this._ctx.scale(2, 2);
        this._ctx.translate(-b.min.x, -b.min.y);
        this.fire('update');
    };
}

const installed = true;
export default installed;
